// src/components/ai/AiButton.js
import React from 'react';

/**
 * Reusable trigger button for AI-assisted features.
 *
 * Props:
 *   onClick       — callback (no args)
 *   loading       — bool; shows spinner + disables button
 *   disabled      — bool; disables without spinner
 *   label         — button text (default "AI Assist")
 *   ghost         — bool; renders as outlined variant instead of filled
 *   className     — extra Tailwind classes for the wrapper button
 */
function AiButton({ onClick, loading = false, disabled = false, label = 'AI Assist', ghost = false, className = '' }) {
  const isDisabled = disabled || loading;

  const baseClass = ghost
    ? 'inline-flex items-center gap-1.5 rounded-full border border-violet-300 px-3 py-1.5 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-50'
    : 'inline-flex items-center gap-1.5 rounded-full bg-violet-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <button
      type="button"
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      className={`${baseClass} ${className}`.trim()}
    >
      {loading ? (
        <svg
          aria-hidden="true"
          className="h-3 w-3 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      ) : (
        <svg aria-hidden="true" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m3.343-5.657-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )}
      {loading ? 'Thinking…' : label}
    </button>
  );
}

export default AiButton;
